import { formatTaskDate } from '../../../utils/taskDate';

// ============================================================
// Column names must exactly match the column `name` property
// used in MyTaskTable.js columns state array.
// ============================================================

/** Columns that can be edited inline (single-click in table cell) */
export const MYTASK_INLINE_COLUMNS = ['status', 'priority', 'dueDate'];

// Task domain enums – mirrors task.model.js constants
export const TASK_STATUSES = [
  'Draft',
  'Assigned',
  'Pending',
  'Submitted for Approval',
  'Complete',
  'On Hold',
  'Cancelled'
];

export const TASK_PRIORITIES = ['Critical', 'High', 'Normal', 'Low'];

export const TASK_TYPES = [
  'Site Item',
  'Defect',
  'RFI',
  'Design Change',
  'Variation',
  'Estimate',
  'Service Project',
  'Internal Office'
];

export const TASK_PENDING_REASONS = [
  'Information',
  'Materials',
  'Approval',
  'Access',
  'Client',
  'Builder',
  'Consultant',
  'Supplier',
  'Another trade',
  'Internal decision',
  'Dependency not complete',
  'Other'
];

export const TASK_CRITICAL_REASONS = [
  'Safety issue',
  'Compliance issue',
  'Client shutdown',
  'Live-service failure',
  'Immediate programme impact',
  'Major defect',
  'Urgent commercial deadline',
  'Other'
];

/** Open statuses shown by default (Complete and Cancelled are hidden) */
export const OPEN_TASK_STATUSES = ['Assigned', 'Pending', 'Complete'];

// ============================================================
// Validation
// ============================================================

export const requiredRule = {
  isValid: (value) => (value?.toString()?.trim()?.length ?? 0) > 0,
  errorText: 'This field is required'
};

/** Validation rules for inline editable columns */
export const validationRules = {
  status: requiredRule,
  priority: requiredRule,
  dueDate: requiredRule
};

export const validateMyTaskChanges = (changed) =>
  Object.fromEntries(
    Object.entries(changed || {}).map(([rowId, rowChanges]) => [
      rowId,
      Object.fromEntries(
        Object.entries(rowChanges || {})
          .filter(([field]) => validationRules[field])
          .map(([field, value]) => {
            const rule = validationRules[field];
            const isValid = Boolean(rule.isValid(value));
            return [field, { isValid, error: isValid ? null : rule.errorText }];
          })
      )
    ])
  );

export const hasMyTaskValidationErrors = (changed, status) =>
  Object.keys(changed || {}).some((rowId) =>
    Object.keys(changed[rowId] || {}).some((field) => status[rowId]?.[field]?.isValid === false)
  );

// ============================================================
// Row mappers – flatten nested Mongo docs into flat table rows
// ============================================================

/** Map an assigned-to list to a display string */
export const formatAssignedTo = (assignedTo = []) =>
  assignedTo
    .map((a) => a?.employeeId?.DisplayName || a?.employeeId?.FirstName || '')
    .filter(Boolean)
    .join(', ');

/** Map supporting staff to a display string */
export const formatSupportingStaff = (supportingStaff = [], externalRecipients = []) =>
  [
    ...supportingStaff.map((s) => s?.employeeId?.DisplayName || s?.employeeId?.FirstName || ''),
    ...externalRecipients
      .filter((recipient) => !recipient.role || recipient.role === 'supportingStaff')
      .map((recipient) => recipient.name || recipient.email || '')
  ]
    .filter(Boolean)
    .join(', ');

/** Flatten a raw task document into a flat table row */
export const mapTaskToRow = (task) => ({
  id: task._id,
  _id: task._id,
  taskViewedByAssignee: task.taskViewedByAssignee ? 'Viewed' : 'Not Viewed',
  taskViewedBySupporter: task.taskViewedBySupporter ? 'Viewed' : 'Not Viewed',
  title: task.title || '',
  description: task.description || '',
  type: task.type || '',
  projectName: task.project?.name || '',
  class: task.class?.name || '',
  location: task.location || '',
  priority: task.priority || 'Normal',
  status: task.status || 'Draft',
  estimatedHours: task.estimatedHours ?? '',
  actualHours: task.actualHours ?? '',
  startTime: task.startTime || '',
  createdBy: task.createdBy?.DisplayName || task.createdBy?.FirstName || '',
  createdDate: formatTaskDate(task.createdDate, ''),
  assignedTo: formatAssignedTo(task.assignedTo),
  supportingStaff: formatSupportingStaff(task.supportingStaff, task.externalRecipients),
  dueDate: formatTaskDate(task.dueDate, ''),
  pendingReason: task.workflow?.pendingReason || '',
  pendingComment: task.workflow?.pendingComment || '',
  cancellationReason: task.workflow?.cancellationReason || '',
  rejectionReason: task.workflow?.rejectionReason || '',
  submittedDate: formatTaskDate(task.workflow?.approval?.submittedDate, ''),
  approvedDate: task.workflow?.approval?.approvedDate
    ? new Date(task.workflow.approval.approvedDate).toLocaleDateString()
    : '',
  approvalNotes: task.workflow?.approval?.approvalNotes || '',
  completedDate: task.workflow?.completion?.completedDate
    ? new Date(task.workflow.completion.completedDate).toLocaleDateString()
    : '',
  completionNotes: task.workflow?.completion?.completionNotes || '',
  dependencies: (task.dependencies || [])
    .map((dependency) => dependency?.title)
    .filter(Boolean)
    .join(', '),
  dependencyIds: (task.dependencies || []).map((dependency) => dependency?._id || dependency).filter(Boolean),
  dependencyStatus: task.dependencies?.length ? 'Has dependencies' : 'None',
  updatedAt: formatTaskDate(task.updatedAt, ''),
  // Keep the raw doc for popup editing
  _raw: task
});

export const statusColors = {
  Draft: { color: '#616161', background: '#eeeeee' },
  Assigned: { color: '#1565c0', background: '#e3f2fd' },
  Pending: { color: '#c62828', background: '#ffebee' },
  'Submitted for Approval': { color: '#7b1fa2', background: '#f3e5f5' },
  Complete: { color: '#2e7d32', background: '#e8f5e9' },
  'On Hold': { color: '#5d4037', background: '#efebe9' },
  Cancelled: { color: '#455a64', background: '#eceff1' }
};

export const priorityColors = {
  Critical: { color: '#c62828', background: '#ffebee' },
  High: { color: '#e65100', background: '#fff3e0' },
  Normal: { color: '#1565c0', background: '#e3f2fd' },
  Low: { color: '#616161', background: '#f5f5f5' }
};

export const mapEmployees = (employee) => ({
  id: employee._id,
  label: employee.DisplayName,
  value: employee._id
});

export const createsDependencyCycle = (taskId, candidateId, taskOptions = []) => {
  const targetId = String(taskId);
  const pendingIds = [String(candidateId)];
  const visited = [];

  while (pendingIds.length) {
    const currentId = pendingIds.pop();
    if (currentId === targetId) return true;
    if (visited.includes(currentId)) continue;
    visited.push(currentId);
    const option = taskOptions.find((taskOption) => String(taskOption.value) === currentId);
    (option?.dependencyIds || []).forEach((dependencyId) => pendingIds.push(String(dependencyId)));
  }

  return false;
};
